import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import clsx from 'clsx';
import { Grid, Typography, Link, Hidden, ButtonGroup } from '@material-ui/core';
import Truncate from 'react-truncate';

// CONSTANTS
import {
  ROUTE_DISCOVER,
  ROUTE_DISCOVER_MOVIE,
  ROUTE_SHOWS_AND_MOVIES,
  ROUTE_SHOWS_AND_MOVIES_MOVIE,
} from '@/constants/routes';
import { contentStatuses, DisplayType } from '@/constants/watchList';
import {
  LOCATIONS_CONTENT_MODAL,
  LOCATIONS_MODAL,
  LOCATIONS_MOVIE_DETAILS_PAGE,
} from '@/constants/locations';
import { actions, triggered } from '@/constants/analytics';

// UTILITY
import { getLink } from '@/utility/routes';
import { getContentYear, getPlaceholderUrl, getDuration } from '@/utility/content';

// STORE
import { AppActions } from '@/store/actions';

// HOC
import withMediaQuery from '@/hoc/withMediaQuery';
import Guard from '@/hoc/Guard';
import Measure from '@/hoc/Measure';
import withRouter from '@/hoc/withRouter';

// COMPONENTS
import Loader from '@/components/Loader';
import DetailsButton from '@/components/Cards/ShowMovieCard/help/DetailsButton';
import RouterLink from '@/components/Link';
import RouterBreadcrumbs from '@/components/RouterBreadcrumbs';
import AddRemoveButton from '@/components/Buttons/AddRemoveButton';
import InfoList from '@/components/ShowMovies/InfoList';
import Scores from '@/components/ShowMovies/Scores';
import Genres from '@/components/ShowMovies/Genres';
import WhereToWatch from '@/components/ShowMovies/WhereToWatch';
import ShowMoreText from '@/components/ShowMoreText';
import ShowMovieVotes from '@/components/ShowMovies/ShowMovieVotes';
import DialogReport from '@/components/Dialogs/DialogReport';

// STYLES
import { Container } from '../../styles';
import {
  Wrapper,
  // VideoWrapper
} from './styles';

// HELP
// import Modal from '../Modal';

class Hero extends PureComponent {
  state = {
    reportIsOpen: false,
  };

  handleReportDialogState = state => {
    this.setState({ reportIsOpen: state });
  };

  render() {
    const { reportIsOpen } = this.state;
    const {
      t,
      router: { routerMatch },
      data,
      sameContent,
      partnerData,
      loadingAction,
      addToWatchList,
      removeFromWatchList,
      changeContentStatus,
      matches,
      isAuth,
      displayType,
      streamingData,
      streamingDataLoading,
      analyticsLocation,
      createFeedback,
    } = this.props;
    const isPage = displayType === DisplayType.PAGE;
    const isDialog = displayType === DisplayType.DIALOG;

    const sameContentLength = sameContent.length;
    const hasPartner = !!Object.keys(partnerData).length;
    const content_status = data?.movie?.user_content_status;
    const inWatchlist = data?.movie.in_watchlist;
    const isWatched = content_status === contentStatuses.watched;
    const isNotInterested = content_status === contentStatuses.notInterested;
    const exclude = ['movie'];
    const votes = data?.movie.votes;
    const hasVotes = !!votes;
    const isShowMoviesMovieRoute = routerMatch({ path: ROUTE_SHOWS_AND_MOVIES_MOVIE });
    let use_marketplace = true;
    const movieStreamingData = streamingData
      ? [...(streamingData.apps || []), ...(streamingData['apps-rent-purchase'] || [])]
      : [];

    if (hasPartner) {
      use_marketplace = !!partnerData.settings?.use_marketplace;
    }

    const contentInfo = [
      { title: t('common.movie.rated'), value: data.movie.rating },
      // { title: t('common.movie.imdb'), value: data.movie.rating_imdb },
      {
        title: t('common.movie.length'),
        value: data.movie.duration ? getDuration(data.movie.duration) : '',
      },
    ];

    return (
      <Wrapper className={clsx('section-wrapper hero-section', `hero-section--${displayType}`)}>
        <div className="page-content">
          {isPage && use_marketplace && (
            <>
              <Hidden xsDown>
                <RouterBreadcrumbs
                  className="breadcrumbs breadcrumbs--dark hero-section__breadcrumbs"
                  lastBreadcrumbText={data.movie.content_title}
                  exclude={exclude}
                />
              </Hidden>
              <Hidden smUp>
                <Link
                  className="p5 hero-section__back-link"
                  color="textSecondary"
                  component={RouterLink}
                  to={isShowMoviesMovieRoute ? ROUTE_SHOWS_AND_MOVIES : ROUTE_DISCOVER}
                  location={LOCATIONS_MOVIE_DETAILS_PAGE}
                >
                  <i className="icon-chevron-left" />
                  {t(`common.menu.${isShowMoviesMovieRoute ? 'tvShowsAndMovies' : 'explore'}`)}
                </Link>
              </Hidden>
            </>
          )}
          <Container className="main-content">
            <div className="hero-section__content">
              <Grid container spacing={4}>
                <Grid item xs={12} md={isPage ? 5 : 4}>
                  <div className="hero-section__left-section">
                    <div
                      className={clsx(
                        'hero-section__content-image-wrapper',
                        hasVotes && 'content__votes',
                      )}
                    >
                      <img
                        className="hero-section__content-image"
                        src={
                          data.movie.thumbnail ||
                          getPlaceholderUrl({
                            title: data.movie.content_title,
                            width: 355,
                            height: 530,
                          })
                        }
                        alt="film-poster"
                      />
                      {hasVotes && <ShowMovieVotes data={votes} />}
                    </div>
                    {/* {data.movie.trailer && ( */}
                    {/*  <Button */}
                    {/*    className="hero-section__content-trailer-button" */}
                    {/*    color="primary" */}
                    {/*    target="_blank" */}
                    {/*    href={data.movie.trailer} */}
                    {/*    // onClick={this.handleOpen} */}
                    {/*  > */}
                    {/*    {t('buttons.watchTrailer')} */}
                    {/*  </Button> */}
                    {/* )} */}
                    <Guard
                      iframe={false}
                      partner={{
                        search_movies_shows: true,
                        use_authorizing: true,
                      }}
                    >
                      <>
                        <AddRemoveButton
                          className="hero-section__content-button sizeExtraLarge"
                          variant="outlined"
                          color="primary"
                          disabled={loadingAction}
                          onClick={() => addToWatchList(data.movie)}
                          handleDelete={() => removeFromWatchList(data.movie)}
                          added={inWatchlist}
                          minWidth={171}
                          removeText={t('buttons.removeFromWatchlist')}
                          checkAuth
                          checkAuthProps={{
                            authComponentProps: {
                              analyticsData: {
                                triggered: triggered.movie,
                                action: actions.addToWatchlist,
                                content_id: data.movie.id,
                                partner_id: partnerData.id || 0,
                                location: analyticsLocation,
                              },
                            },
                          }}
                        >
                          {t(`buttons.${inWatchlist ? 'addedToWatchlist' : 'addToWatchlist'}`)}
                        </AddRemoveButton>
                        <ButtonGroup
                          className="hero-section__content-buttons type-secondary"
                          variant="contained"
                        >
                          <AddRemoveButton
                            className={clsx('sizeExtraLarge', !isWatched && 'grey')}
                            disabled={loadingAction}
                            onClick={() =>
                              changeContentStatus(data.movie.id, contentStatuses.watched)
                            }
                            handleDelete={() => changeContentStatus(data.movie.id, null)}
                            added={isWatched}
                            checkAuth
                            minWidth={145}
                            status={contentStatuses.watched}
                          >
                            {t('buttons.watched')}
                          </AddRemoveButton>
                          <AddRemoveButton
                            className={clsx('sizeExtraLarge', !isNotInterested && 'grey')}
                            disabled={loadingAction}
                            onClick={() =>
                              changeContentStatus(data.movie.id, contentStatuses.notInterested)
                            }
                            handleDelete={() => changeContentStatus(data.movie.id, null)}
                            added={isNotInterested}
                            checkAuth
                            minWidth={145}
                            status={contentStatuses.notInterested}
                          >
                            {t('buttons.notInterested')}
                          </AddRemoveButton>
                        </ButtonGroup>
                        {isDialog && (
                          <DetailsButton
                            location={LOCATIONS_CONTENT_MODAL}
                            data={data.movie}
                            hasStreamingData={!streamingDataLoading && !!movieStreamingData.length}
                            className="sizeExtraLarge hero-section__content-buttons"
                            variant="contained"
                            color="primary"
                            fullWidth
                          >
                            {t('buttons.seeDetails')}
                          </DetailsButton>
                        )}
                      </>
                    </Guard>
                  </div>
                </Grid>
                <Grid item xs={12} md={isPage ? 7 : 8}>
                  <Typography
                    className={clsx('title', isPage && sameContentLength ? 'm-b-5' : 'm-b-15')}
                    variant={matches || isDialog ? 'h3' : 'h2'}
                  >
                    {data.movie.content_title}
                    &nbsp;
                    {data.movie.first_air_date && (
                      <Typography
                        className={isPage ? 'p1' : 'p4'}
                        component="span"
                        color="textSecondary"
                      >
                        ({getContentYear(data.movie.first_air_date)})
                      </Typography>
                    )}
                  </Typography>

                  {isPage && !!sameContentLength && (
                    <Typography className="m-b-15" variant="body1">
                      {t('movieDetails.hero.searchedFor')}
                      &nbsp;
                      {sameContent.map((movie, index) => (
                        <React.Fragment key={movie.id}>
                          <Link
                            variant="inherit"
                            component={RouterLink}
                            to={getLink(
                              isShowMoviesMovieRoute
                                ? ROUTE_SHOWS_AND_MOVIES_MOVIE
                                : ROUTE_DISCOVER_MOVIE,
                              { movie_id: movie.slug || movie.id },
                            )}
                            location={LOCATIONS_MOVIE_DETAILS_PAGE}
                          >
                            {movie.content_title}{' '}
                            {movie.first_air_date
                              ? `(${getContentYear(movie.first_air_date)})`
                              : ''}
                          </Link>
                          {sameContentLength - 1 > index && (
                            <>
                              &nbsp;
                              {t('common.or')}
                              &nbsp;
                            </>
                          )}
                        </React.Fragment>
                      ))}
                      ?
                    </Typography>
                  )}

                  <Scores
                    imdb={data.movie?.imdb_rating || ''}
                    rTomatoes={data.movie?.rotten_tomatoes || ''}
                    mbRating={data.movie?.average_user_rating?.toString() || ''}
                  />

                  <Genres data={data.movie.genres || []} />

                  <InfoList className="m-b-15" data={contentInfo} />

                  {data.movie.overview &&
                    (isDialog ? (
                      <Measure>
                        {([ref, { width }]) => (
                          <Typography
                            ref={ref}
                            className={clsx('hero-section__content-description', isDialog && 'p4')}
                          >
                            <Truncate lines={4} trimWhitespace width={width}>
                              {data.movie.overview}
                            </Truncate>
                          </Typography>
                        )}
                      </Measure>
                    ) : (
                      <ShowMoreText openOnce>
                        <Typography
                          className={clsx('hero-section__content-description', isDialog && 'p4')}
                        >
                          {data.movie.overview}
                        </Typography>
                      </ShowMoreText>
                    ))}

                  {streamingDataLoading ? (
                    <div className="m-b-30 m-t-30">
                      <Loader active position="relative" />
                    </div>
                  ) : (
                    <WhereToWatch
                      type="movie"
                      content={data.movie}
                      data={movieStreamingData}
                      className={clsx(
                        'where-to-watch__section',
                        !movieStreamingData.length && 'where-to-watch__no-results',
                      )}
                      subscribed={isAuth && !!data?.movie.subscription_status}
                      changeContentStatus={changeContentStatus}
                      analyticsLocation={isDialog ? LOCATIONS_MODAL : ''}
                    />
                  )}
                  {!streamingDataLoading && (
                    <Link
                      className="hero-section__report-link"
                      component="button"
                      color="secondary"
                      onClick={() => this.handleReportDialogState(true)}
                    >
                      {t('buttons.letUsKnow')}
                    </Link>
                  )}
                </Grid>
              </Grid>
            </div>
          </Container>
        </div>
        <DialogReport
          open={reportIsOpen}
          data={{
            type: 'content',
            contentType: 'film',
            entity_id: data.movie?.id,
            content_title: data.movie.content_title,
          }}
          onClose={() => this.handleReportDialogState(false)}
          onSubmit={createFeedback}
          analyticsLocation={analyticsLocation}
        />
        {/* <Modal open={open} onClose={this.handleClose}> */}
        {/*  <VideoWrapper> */}
        {/*    <div className="video-wrapper"> */}
        {/*      <iframe */}
        {/*        src="http://demo.guidebox.com/embed.php?video=50663" */}
        {/*        // src={data.movie.trailer} */}
        {/*        frameBorder="0" */}
        {/*        allowFullScreen */}
        {/*        title="video" */}
        {/*        allow="autoplay; encrypted-media" */}
        {/*      /> */}
        {/*    </div> */}
        {/*  </VideoWrapper> */}
        {/* </Modal> */}
      </Wrapper>
    );
  }
}

Hero.defaultProps = {
  streamingDataLoading: false,
  displayType: DisplayType.PAGE,
  sameContent: [],
  loadingAction: false,
};

Hero.propTypes = {
  t: PropTypes.func.isRequired,
  router: PropTypes.object.isRequired,
  data: PropTypes.object.isRequired,
  streamingData: PropTypes.object,
  sameContent: PropTypes.array,
  partnerData: PropTypes.object.isRequired,
  loadingAction: PropTypes.bool,
  addToWatchList: PropTypes.func.isRequired,
  removeFromWatchList: PropTypes.func.isRequired,
  changeContentStatus: PropTypes.func.isRequired,
  matches: PropTypes.bool.isRequired,
  isAuth: PropTypes.bool.isRequired,
  streamingDataLoading: PropTypes.bool,
  displayType: PropTypes.string,
  analyticsLocation: PropTypes.string,
  createFeedback: PropTypes.func.isRequired,
};

const mapDispatchToProps = dispatch => {
  return {
    createFeedback: (data, callback) => {
      dispatch(AppActions.createFeedback.action({ data, callback }));
    },
  };
};

export default compose(
  connect(null, mapDispatchToProps),
  withTranslation(),
  withMediaQuery({ matches: theme => theme.breakpoints.down('xs') }),
  withRouter,
)(Hero);
